import { Component, inject, signal } from '@angular/core';
import { form, FormField, FormRoot, min, submit } from '@angular/forms/signals';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { Currency } from '@app/shared/types/enums/currency.enum';
import { QuantityUnits } from '@app/shared/types/enums/quantity-units.enum';
import { TimeUnits } from '@app/shared/types/enums/time-units.enum';
import { DropdownItem } from '@app/shared/types/models/shared/dropdown-item';
import { TaskEstimation } from '@app/shared/types/models/task/task-estimation';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiInputComponent } from '@app/shared/ui/input/ui-input.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { amountAndUnitToDuration, durationToHours } from '@app/shared/utils/time-utils';
import { TranslatePipe } from '@ngx-translate/core';

interface EstimationFormModel {
  timeEstimate: { amount: number | null; units: TimeUnits };
  costEstimate: { amount: number | null; currency: Currency };
  quantityEstimate: { amount: number | null; units: QuantityUnits };
}

function buildInitialModel(estimation?: TaskEstimation): EstimationFormModel {
  const model: EstimationFormModel = {
    timeEstimate: { amount: null, units: TimeUnits.HOURS },
    costEstimate: { amount: null, currency: Currency.NOK },
    quantityEstimate: { amount: null, units: QuantityUnits.PIECES }
  };

  if (estimation?.time) {
    model.timeEstimate = {
      amount: durationToHours(estimation.time),
      units: TimeUnits.HOURS
    };
  }

  if (estimation?.financial?.currency) {
    model.costEstimate = {
      amount: estimation.financial.amount,
      currency: estimation.financial.currency
    };
  }

  if (estimation?.quantity?.unit) {
    model.quantityEstimate = {
      amount: estimation.quantity.amount,
      units: estimation.quantity.unit
    };
  }

  return model;
}

export interface EstimationDialogData {
  estimation?: TaskEstimation;
  showCost?: boolean;
  showTime?: boolean;
  autoFocus: boolean;
}

@Component({
  selector: 'proffeo-estimation-dialog',
  templateUrl: './estimation-dialog.component.html',
  imports: [
    MatIconModule,
    FormField,
    FormRoot,
    TranslatePipe,
    MatDialogModule,
    UiInputComponent,
    UiSelectComponent,
    UiButtonComponent,
    UiDialogComponent
  ]
})
export class EstimationDialogComponent {
  private readonly dialogRef: MatDialogRef<EstimationDialogComponent> = inject(MatDialogRef<EstimationDialogComponent>);
  private readonly modalData: EstimationDialogData = inject(MAT_DIALOG_DATA);

  protected readonly estimationModel = signal<EstimationFormModel>(buildInitialModel(this.modalData.estimation));

  protected readonly estimateForm = form(this.estimationModel, path => {
    min(path.timeEstimate.amount, 0);
    min(path.costEstimate.amount, 0);
    min(path.quantityEstimate.amount, 0);
  });

  protected readonly timeUnitOptions: DropdownItem<TimeUnits>[] = [
    { label: 'time-units.hours', value: TimeUnits.HOURS },
    { label: 'time-units.minutes', value: TimeUnits.MINUTES },
    { label: 'time-units.man-days', value: TimeUnits.MAN_DAYS }
  ];

  protected readonly currencyOptions: DropdownItem<Currency>[] = [
    { label: 'NOK', value: Currency.NOK },
    { label: 'PLN', value: Currency.PLN },
    { label: 'EUR', value: Currency.EUR },
    { label: 'USD', value: Currency.USD }
  ];
  protected readonly quantityUnitOptions: DropdownItem<QuantityUnits>[] = [
    { label: 'quantity-units.pieces', value: QuantityUnits.PIECES },
    { label: 'quantity-units.meters', value: QuantityUnits.METERS },
    { label: 'quantity-units.meters-square', value: QuantityUnits.METERS_SQUARE },
    { label: 'quantity-units.meters-cubic', value: QuantityUnits.METERS_CUBIC },
    { label: 'quantity-units.sets', value: QuantityUnits.SETS }
  ];

  protected saveEstimation(): void {
    void submit(this.estimateForm, {
      action: async () => {
        const { amount: timeAmount, units } = this.estimationModel().timeEstimate;
        const { amount: costAmount, currency } = this.estimationModel().costEstimate;
        const { amount: quantityAmount, units: quantityUnits } = this.estimationModel().quantityEstimate;

        const result: TaskEstimation = {
          time: amountAndUnitToDuration(timeAmount, units) ?? null,
          financial: { amount: costAmount, currency },
          quantity: { amount: quantityAmount, unit: quantityUnits }
        };

        this.dialogRef.close(result);
      }
    });
  }

  protected cancelEstimation(): void {
    this.dialogRef.close();
  }
}
